import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ForcedPasswordSetup — mandatory "set your password" screen (AL-M3, ST9).
 *
 * Rendered by the hub <AuthGate> when `user.hasPassword === false` (a
 * federated-only user). Blocks everything: focus-trapped dialog, Esc/backdrop
 * are no-ops. On success it force-refreshes tokens so the new ID token carries
 * `custom:has_password='true'` and the gate clears in place — no re-login
 * (set-initial does not revoke the session; see DESIGN §4.4 Q1).
 *
 * Spec: docs/AL-M3/DESIGN.md §4.4.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Lock, ShieldCheck } from "lucide-react";
import { useAuth } from "../AuthProvider.js";
import { getPasswordRules } from "../passwordRules.js";
import { refreshAndGetToken } from "../sessionRefresher.js";
import { setInitialPassword } from "../api.js";
import { MAX_PASSWORD_LENGTH } from "../inputLimits.js";
import { AuthAlert } from "./AuthAlert.js";
import { AuthField } from "./AuthField.js";
import { PasswordChecklist } from "./PasswordChecklist.js";
export function ForcedPasswordSetup({ apiBase, wrapper: Wrapper, onComplete, ns = "common", }) {
    const { t } = useTranslation(ns);
    const { user, accessToken, refreshSession } = useAuth();
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const dialogRef = useRef(null);
    const rules = useMemo(() => getPasswordRules(password), [password]);
    const canSubmit = rules.every((r) => r.valid) && !submitting;
    // Initial focus on the password field.
    useEffect(() => {
        document.getElementById("forced-password")?.focus();
    }, []);
    // Minimal focus trap: keep Tab inside the dialog; Esc is a no-op (mandatory).
    const onKeyDown = (e) => {
        if (e.key === "Escape") {
            e.preventDefault();
            return;
        }
        if (e.key !== "Tab" || !dialogRef.current)
            return;
        const focusables = dialogRef.current.querySelectorAll('button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])');
        if (focusables.length === 0)
            return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        }
        else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };
    const handleSubmit = async () => {
        if (!canSubmit || !accessToken)
            return;
        setSubmitting(true);
        setError(null);
        const res = await setInitialPassword(apiBase, accessToken, password);
        // 409 password_already_set is idempotent success — refresh + clear gate.
        if (res.ok || res.status === 409) {
            try {
                await refreshAndGetToken(); // forceRefresh → new ID token has the flag
                await refreshSession(); // AuthProvider re-reads claims; gate clears
                onComplete?.();
            }
            catch {
                setError("refresh_failed");
                setSubmitting(false);
            }
            return;
        }
        if (res.status === 422)
            setError("password_policy_violation");
        else if (res.status === 429)
            setError("rate_limited");
        else
            setError("set_password_failed");
        setSubmitting(false);
    };
    const content = (_jsx("div", { className: "flex min-h-[calc(100vh-128px)] items-center justify-center px-4", children: _jsxs("div", { ref: dialogRef, role: "dialog", "aria-modal": "true", "aria-labelledby": "forced-pw-title", onKeyDown: onKeyDown, className: "w-full max-w-md rounded-3xl border border-slate-200 bg-white px-6 py-8 shadow-xl", children: [_jsx("div", { className: "mx-auto flex size-12 items-center justify-center rounded-full bg-violet-100 text-violet-600", children: _jsx(ShieldCheck, { className: "size-5" }) }), _jsx("h1", { id: "forced-pw-title", className: "mt-4 text-center text-2xl font-semibold text-slate-900", children: t("forcedPassword.title") }), _jsx("p", { className: "mt-2 text-center text-sm text-slate-600", children: t("forcedPassword.subtitle") }), user?.email ? (_jsx("p", { className: "mt-2 text-center text-sm font-medium text-slate-900", children: t("forcedPassword.signedInAs", { email: user.email }) })) : null, error ? (_jsx("div", { className: "mt-4", children: _jsx(AuthAlert, { variant: "error", children: t(`forcedPassword.error.${error}`) }) })) : null, _jsxs("div", { className: "mt-6 space-y-4", children: [_jsx(AuthField, { id: "forced-password", label: t("forcedPassword.passwordLabel"), type: showPassword ? "text" : "password", autoComplete: "new-password", placeholder: t("forcedPassword.passwordPlaceholder"), value: password, onChange: setPassword, icon: _jsx(Lock, { className: "size-4" }), maxLength: MAX_PASSWORD_LENGTH, rightSlot: _jsx("button", { type: "button", "aria-label": showPassword ? t("auth.hidePassword") : t("auth.showPassword"), onClick: () => setShowPassword((p) => !p), className: "transition hover:text-slate-600", children: showPassword ? _jsx(EyeOff, { className: "size-4" }) : _jsx(Eye, { className: "size-4" }) }) }), _jsx(PasswordChecklist, { rules: rules }), _jsx("button", { type: "button", onClick: handleSubmit, disabled: !canSubmit, className: "h-11 w-full rounded-xl text-sm font-medium text-white transition-colors bg-violet-600 hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50", children: submitting ? t("forcedPassword.saving") : t("forcedPassword.submit") })] })] }) }));
    return Wrapper ? _jsx(Wrapper, { children: content }) : content;
}
